/**
 * Item-centric search result card.
 * Shows: matched items → box name → location.
 * Highlights query matches in text.
 */

import type { SearchResult } from '../hooks/useSearch';
import { HighlightMatch } from './HighlightMatch';

interface SearchResultCardProps {
  result: SearchResult;
  query: string;
  onClick: (boxId: string) => void;
}

export function SearchResultCard({ result, query, onClick }: SearchResultCardProps) {
  const { box, matchedItems, matchType } = result;

  return (
    <button
      type="button"
      onClick={() => onClick(box.id)}
      className="w-full rounded-xl border border-border bg-surface p-4 text-left hover:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background transition-colors"
      data-testid={`search-result-${box.id}`}
    >
      {/* Matched items (primary for item matches) */}
      {matchType === 'item' && matchedItems.length > 0 && (
        <div className="mb-2">
          {matchedItems.slice(0, 3).map((item) => (
            <div key={item.id} className="text-sm text-text-primary">
              <HighlightMatch text={item.name} query={query} />
              {item.quantity > 1 && (
                <span className="ml-1 text-xs text-text-secondary">x{item.quantity}</span>
              )}
            </div>
          ))}
          {matchedItems.length > 3 && (
            <p className="text-xs text-text-secondary mt-0.5">
              +{matchedItems.length - 3} more item{matchedItems.length - 3 !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {/* Box name + location line */}
      <div className="flex items-center gap-2 text-xs">
        {/* Box icon */}
        <svg
          className="h-3.5 w-3.5 shrink-0 text-text-secondary"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>

        <span
          className={`font-medium truncate ${matchType === 'box' ? 'text-text-primary' : 'text-text-secondary'}`}
        >
          <HighlightMatch text={box.name} query={query} />
        </span>

        {/* Marker badge */}
        <span className="shrink-0 rounded bg-accent/10 px-1 py-0.5 text-[10px] font-medium text-accent uppercase">
          {box.markerType === 'aruco' ? `#${box.markerId}` : 'QR'}
        </span>

        {/* Location */}
        {box.location && (
          <>
            <span className="text-text-secondary/50">in</span>
            <span
              className={`truncate ${matchType === 'location' ? 'text-text-primary font-medium' : 'text-text-secondary'}`}
            >
              <HighlightMatch text={box.location} query={query} />
            </span>
          </>
        )}
      </div>

      {/* Non-matched items summary (for box/location matches) */}
      {matchType !== 'item' && box.items.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {box.items.slice(0, 4).map((item) => (
            <span
              key={item.id}
              className="rounded-full bg-surface px-2 py-0.5 text-[10px] text-text-secondary border border-border"
            >
              {item.name}
            </span>
          ))}
          {box.items.length > 4 && (
            <span className="text-[10px] text-text-secondary/60 py-0.5">
              +{box.items.length - 4} more
            </span>
          )}
        </div>
      )}
    </button>
  );
}
